import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight, Sparkles } from "lucide-react";
import { getArticle, ARTICLES } from "../content/articles";
import { useSeo } from "../hooks/useSeo";

/**
 * Public individual article — /advice/:slug.
 *
 * Fully SSR-friendly plain-text render (single React component, no data fetch).
 * Injects per-article <title>, meta description, canonical, and og:tags.
 */
function renderBlock(block, i) {
  switch (block.type) {
    case "h2":
      return (
        <h2 key={i} className="mt-10 font-serif-display text-3xl md:text-4xl text-[#1a1a1a]">
          {block.text}
        </h2>
      );
    case "p":
      return (
        <p key={i} className="mt-5 text-lg text-[#1a1a1a] leading-[1.8]">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={i} className="mt-5 space-y-2 text-lg text-[#1a1a1a] list-disc pl-6 leading-[1.7]">
          {block.items.map((item, j) => <li key={j}>{item}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol key={i} className="mt-5 space-y-2 text-lg text-[#1a1a1a] list-decimal pl-6 leading-[1.7]">
          {block.items.map((item, j) => <li key={j}>{item}</li>)}
        </ol>
      );
    case "quote":
      return (
        <blockquote
          key={i}
          className="mt-8 pl-6 border-l-4 border-[#c39d2b] text-xl italic text-[#5c5c5c] leading-relaxed"
        >
          {block.text}
        </blockquote>
      );
    case "cta":
      return (
        <div key={i} className="mt-10 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8] p-6 md:p-8" data-testid="article-inline-cta">
          <p className="text-lg text-[#1a1a1a]">{block.text}</p>
          <Link
            to={block.href || "/register"}
            className="btn-primary mt-4 inline-flex items-center gap-2 !py-3 !px-5"
            data-testid="article-inline-cta-btn"
          >
            {block.label || "Get started"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    default:
      return null;
  }
}

export default function AdviceArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  // Always call hooks unconditionally
  useSeo({
    title: article ? `${article.title} · Prime Connect` : "Advice · Prime Connect",
    description: article?.description,
    canonical: article ? `https://primeconnect55.com/advice/${article.slug}` : undefined,
    type: "article",
  });

  if (!article) return <Navigate to="/advice" replace />;

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);
  const publishedNice = new Date(article.published).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <article className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-16" data-testid="advice-article-root">
      <Link
        to="/advice"
        className="inline-flex items-center gap-1.5 text-sm text-[#722f37] hover:underline"
        data-testid="advice-article-back"
      >
        <ArrowLeft className="w-4 h-4" /> All advice
      </Link>

      <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
        <Sparkles className="w-3.5 h-3.5 text-[#c39d2b]" /> {article.cluster}
      </div>
      <h1
        className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight text-[#1a1a1a]"
        data-testid="advice-article-title"
      >
        {article.title}
      </h1>
      <div className="mt-4 flex items-center gap-4 text-xs text-[#5c5c5c]" data-testid="advice-article-meta">
        <span>{publishedNice}</span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" /> {article.read_minutes} min read
        </span>
      </div>

      <p className="mt-6 text-xl text-[#5c5c5c] leading-relaxed" data-testid="advice-article-lede">
        {article.description}
      </p>

      <div className="mt-4 border-t border-[#e5e0d8]" />

      <div data-testid="advice-article-body">
        {article.body.map(renderBlock)}
      </div>

      {/* JSON-LD Article structured data for rich results */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.description,
            "datePublished": article.published,
            "author": { "@type": "Organization", "name": "Prime Connect" },
            "publisher": { "@type": "Organization", "name": "Prime Connect" },
            "keywords": article.keywords.join(", "),
            "mainEntityOfPage": `https://primeconnect55.com/advice/${article.slug}`,
          }),
        }}
      />

      {/* Related articles */}
      {related.length > 0 && (
        <div className="mt-16 pt-10 border-t border-[#e5e0d8]" data-testid="advice-article-related">
          <span className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
            Keep reading
          </span>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/advice/${r.slug}`}
                className="rounded-xl bg-white border border-[#e5e0d8] card-elev p-5 hover:border-[#722f37]/40"
                data-testid={`advice-article-related-${r.slug}`}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#722f37]">
                  {r.cluster}
                </div>
                <h3 className="mt-2 font-serif-display text-xl text-[#1a1a1a] leading-snug">
                  {r.title}
                </h3>
                <div className="mt-3 text-xs text-[#5c5c5c] inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {r.read_minutes} min read
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
